import { T, useT } from '@transifex/react';
import { HStack, Pressable, Text, VStack } from '@vidahealth/vida-ui';
import { Platform } from 'react-native';

import { formatBirthdayForDisplay } from '#modules/localization';
import { CtaButton } from '#modules/ui/CtaButton';
import { XIcon } from '#modules/ui/icons';
import { TextField } from '#modules/ui/TextField';

import type {
  RepeatingListColumn,
  RepeatingListRowState,
} from '../../../../types/assessmentQuestion';
import type { ActiveQuestion } from '../../../../utils/resolveQuestionChoices';

export type RepeatingListFieldProps = {
  question: ActiveQuestion;
  rows: RepeatingListRowState[];
  /** The web half of announcing a row add/remove — this live region's text. */
  statusMessage: string;
  isSubmitting: boolean;
  onUpdateRowValue: (rowId: string, columnName: string, text: string) => void;
  onValidateRowCell: (rowId: string, column: RepeatingListColumn) => void;
  onRemoveRow: (rowId: string) => void;
  /** Present only in grouped mode — see `QuestionFieldHandlers.onAddRowInline`. */
  onAddRowInline?: () => void;
  testIDPrefix?: string;
};

type RepeatingListCellProps = {
  column: RepeatingListColumn;
  row: RepeatingListRowState;
  label: string;
  onChangeText: (text: string) => void;
  onBlur: () => void;
  testID?: string;
};

/**
 * One cell of one row, rendered per its column's `cellType`. The floating
 * label is the same as every other input in the flow (date, account creation,
 * member contact) — it stays visible once filled rather than being replaced,
 * and `TextField` derives the accessible name from it, so no separate
 * accessibilityLabel/aria-label is needed here. It also keeps a card of
 * repeating rows compact, which matters more here than anywhere else: the
 * labels repeat once per column per row.
 */
function RepeatingListCell({
  column,
  row,
  label,
  onChangeText,
  onBlur,
  testID,
}: RepeatingListCellProps) {
  const t = useT();
  const value = row.values[column.name] ?? '';
  const errorMessage = row.errors[column.name];

  if (column.cellType === 'date') {
    return (
      <TextField
        value={value}
        onChangeText={onChangeText}
        onBlur={onBlur}
        format={formatBirthdayForDisplay}
        label={label}
        placeholder={t('MM / DD / YYYY')}
        inputMode='numeric'
        keyboardType='number-pad'
        errorMessage={errorMessage}
        testID={testID}
      />
    );
  }

  if (column.cellType === 'number') {
    return (
      <TextField
        value={value}
        onChangeText={onChangeText}
        onBlur={onBlur}
        label={label}
        inputMode='numeric'
        keyboardType='number-pad'
        errorMessage={errorMessage}
        testID={testID}
      />
    );
  }

  // A plain text cell has no shape/bounds check of its own, but it can still
  // carry the per-row "Enter a value." required message (see
  // evaluateRepeatingListAnswer) — same errorMessage wiring as the date/number
  // branches above, just with no onBlur, since there's no shape to re-check.
  return (
    <TextField
      value={value}
      onChangeText={onChangeText}
      label={label}
      errorMessage={errorMessage}
      testID={testID}
    />
  );
}

/**
 * A `repeatingList` question (SurveyJS `matrixdynamic`): one bordered card per
 * row, each holding one field per authored column, with a remove control once
 * there is more than one row.
 */
export function RepeatingListField({
  question,
  rows,
  statusMessage,
  isSubmitting,
  onUpdateRowValue,
  onValidateRowCell,
  onRemoveRow,
  onAddRowInline,
  testIDPrefix,
}: RepeatingListFieldProps) {
  const t = useT();
  const columns = question.columns ?? [];

  return (
    <VStack space='lg'>
      {/* The web half of announcing a row add/remove (see `announce` in
          useRepeatingListRows): an ARIA live region, whose text a screen reader
          reads when it changes. Web only — on native `announceForAccessibility`
          does the job, and an extra node there would just be one more stop
          for a swipe to land on. Positioned off-screen rather than hidden:
          `display: none` and `aria-hidden` content is not announced at all. */}
      {Platform.OS === 'web' ? (
        <Text
          aria-live='polite'
          className='absolute -left-[9999px]'
          testID={testIDPrefix ? `${testIDPrefix}-status` : undefined}
        >
          {statusMessage}
        </Text>
      ) : null}
      {rows.map((row, index) => (
        <VStack key={row.id} space='md' className='rounded-vui-lg border border-outline-200 p-4'>
          {/* No per-row heading: the only label we could show is a generic
              "Item {n}" — `matrixdynamic` carries no authorable row title
              (the builder's MatrixForm exposes only `columns` + `rowCount`),
              so the same hardcoded word would head every list, medicines or
              medical history alike. The card border separates rows and the
              first column's label carries the row number instead. */}
          {rows.length > 1 ? (
            <HStack className='justify-end'>
              <Pressable
                onPress={() => onRemoveRow(row.id)}
                accessibilityRole='button'
                accessibilityLabel={t('Remove item {number}', { number: index + 1 })}
                // The glyph is 18px, under the 24×24 minimum tap target
                // WCAG 2.2 2.5.8 (AA) asks for, so the control is padded
                // out to 30px. Padding rather than `hitSlop`: hitSlop
                // only exists on native (react-native-web's Pressable
                // has no such prop), and this has to hold on web too.
                // Padding is a real box, so it works on every platform —
                // at the cost of ~12px of row height, which is why the
                // icon itself stays 18px.
                className='p-1.5'
                testID={testIDPrefix ? `${testIDPrefix}-remove-row-${index}` : undefined}
              >
                <XIcon className='text-typography-500' width={18} height={18} />
              </Pressable>
            </HStack>
          ) : null}
          {columns.map((column, columnIndex) => {
            // Once a second row exists the bare column title repeats down
            // the screen ("Medication", "Medication"), so the row number
            // rides on the authored title. Both halves are data (the
            // author's column title and an index), so there is nothing to
            // translate here. Only the first column carries it, which is
            // enough to mark where a row begins without stamping the
            // number on every field in the card.
            const numbered = rows.length > 1 && columnIndex === 0;
            return (
              <RepeatingListCell
                key={column.name}
                column={column}
                row={row}
                label={numbered ? `${column.title} ${index + 1}` : column.title}
                onChangeText={(text) => onUpdateRowValue(row.id, column.name, text)}
                onBlur={() => onValidateRowCell(row.id, column)}
                testID={testIDPrefix ? `${testIDPrefix}-input-${index}-${column.name}` : undefined}
              />
            );
          })}
        </VStack>
      ))}
      {/* Pinned in the sticky footer alongside Continue in single-question
          mode (see SingleQuestionScreen); rendered here instead in grouped
          mode, where the footer holds only the page's one shared Continue and
          each repeatingList question needs its own add-row control in place. */}
      {onAddRowInline ? (
        <CtaButton
          type='secondary'
          isDisabled={isSubmitting}
          onPress={onAddRowInline}
          testID={testIDPrefix ? `${testIDPrefix}-add-row` : undefined}
        >
          <T _str='+ Add another' />
        </CtaButton>
      ) : null}
    </VStack>
  );
}
